<template>
    <v-card flat class="form-ordine">

        <!-- Header -->
        <v-card-title class="form-header pa-4 pb-2">
            <v-row class="ma-0 pa-0">
                <span class="text-h6 font-weight-bold text-truncate">Ordine</span>
                <v-spacer />
                <transition name="fade-autosave">
                    <span v-if="autoSalvatoIn" class="text-caption me-3 d-flex align-center" :class="{
                        'text-medium-emphasis': autoSalvatoIn === 'saving',
                        'text-success': autoSalvatoIn === 'saved',
                        'text-error': autoSalvatoIn === 'error'
                    }">
                        <v-progress-circular v-if="autoSalvatoIn === 'saving'" indeterminate size="12" width="2"
                            class="me-1" />
                        <v-icon v-else-if="autoSalvatoIn === 'saved'" size="14"
                            class="me-1">mdi-check-circle-outline</v-icon>
                        <v-icon v-else-if="autoSalvatoIn === 'error'" size="14"
                            class="me-1">mdi-alert-circle-outline</v-icon>
                        {{ autoSalvatoIn === 'saving' ? 'Salvataggio...' :
                            autoSalvatoIn === 'saved' ? 'Salvato' : 'Errore salvataggio' }}
                    </span>
                </transition>
            </v-row>
            <v-btn color="primary" size="small" :loading="loading" @click="salva" prepend-icon="mdi-content-save">
                Salva
            </v-btn>
        </v-card-title>

        <!-- Skeleton durante caricamento config -->
        <v-card-text v-if="loadingConfig" class="pa-3 pt-2">
            <v-skeleton-loader v-for="i in 3" :key="i" type="article" class="mb-2" />
        </v-card-text>

        <v-card-text v-else class="pa-3 pt-0">
            <v-form ref="formRef">
                <v-expansion-panels v-model="panelAperti" variant="accordion" flat multiple>

                    <!-- Sezioni dinamiche dalla configurazione -->
                    <v-expansion-panel v-for="sezione in sezioniVisibili" :key="sezione.id" eager>

                        <v-expansion-panel-title class="section-title px-3 py-2">
                            <template #default="{ isExpanded }">
                                <v-icon :class="isExpanded ? 'text-primary' : 'text-medium-emphasis'" size="18"
                                    class="me-2">
                                    {{ sezione.icona || 'mdi-tune-variant' }}
                                </v-icon>
                                <span class="text-body-2 font-weight-semibold">{{ sezione.etichetta }}</span>
                                <!-- Chip incompleto su sezione con campi obbligatori vuoti -->
                                <v-chip v-if="sezioneIncompleta(sezione)" color="error" size="x-small" class="ms-2"
                                    variant="tonal">Incompleto</v-chip>
                            </template>
                        </v-expansion-panel-title>

                        <v-expansion-panel-text>
                            <v-row dense class="mt-1">
                                <template v-for="campo in sezione.campi" :key="campo.campoKey">
                                    <!-- Larghezza colonna adattiva in base al tipo -->
                                    <v-col :cols="colsCampo(campo)" :sm="colsCampoSm(campo)">
                                        <component :is="'div'">
                                            <DynamicField :campo="campo" :model-value="datiInterni[campo.campoKey]"
                                                :opzioni="opzioni"
                                                @update:model-value="datiInterni[campo.campoKey] = $event"
                                                :rules="getRules(campo)" />
                                        </component>
                                    </v-col>
                                </template>
                            </v-row>
                        </v-expansion-panel-text>

                    </v-expansion-panel>

                </v-expansion-panels>
            </v-form>
        </v-card-text>

    </v-card>
</template>

<script>
import { ref, watch, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { get_opzioni } from '@/api/api.parametri.js'
import { get_configurazione } from '@/api/config/api.campi.js'
import DynamicField from './DynamicField.vue'

export default {
    name: 'FormOrdine',
    components: { DynamicField },

    props: {
        ordine: { type: Object, required: true },
        loading: { type: Boolean, default: false },
        modulo: { type: String, default: 'serramenti' }
    },

    emits: ['salva'],

    setup(props, { emit }) {
        const formRef = ref(null)
        const datiInterni = ref({ ...props.ordine })
        const loadingConfig = ref(true)

        // Configurazione dinamica dal backend
        const configSezioni = ref([]) // array di sezioni con campi

        // Opzioni per select (già esistente)
        const opzioni = ref({
            essenze: [], serie: [], profili: [], spessori: [],
            verniciature: [], persiane: [], avvolgibili: [],
            antoine: [], zanzariereTipo: [], zanzariereColore: [], tipi: []
        })

        const autoSalvatoIn = ref(null)
        let debounceTimer = null
        let isFirstLoad = true
        // true mentre datiInterni viene risincronizzato dalle props (es. dopo l'aggiunta
        // di un allegato o di una riga misura): in quella finestra l'autosave va ignorato,
        // altrimenti ogni aggiornamento esterno all'ordine genera un salvataggio ridondante
        let isSyncingFromProps = false

        // Indici dei pannelli aperti (tutti di default)
        const panelAperti = ref([])

        // ── Sezioni visibili filtrate ────────────────────────────────────────
        const sezioniVisibili = computed(() =>
            configSezioni.value
                .map(s => ({
                    ...s,
                    // Filtra campi misura_* e non visibili
                    campi: s.campi.filter(c => c.visibile && !c.campoKey.startsWith('misura_'))
                }))
                .filter(s => s.campi.length > 0)
                .sort((a, b) => a.ordinamento - b.ordinamento)
        )

        // ── Caricamento config ───────────────────────────────────────────────
        const caricaConfigurazione = async () => {
            loadingConfig.value = true
            try {
                const [resConfig, resOpzioni] = await Promise.all([
                    get_configurazione(props.modulo),
                    get_opzioni()
                ])

                configSezioni.value = resConfig.data?.sezioni || []
                // Apri tutti i panel di default
                panelAperti.value = configSezioni.value.map((_, i) => i)

                opzioni.value = {
                    essenze: resOpzioni.data.essenze || [],
                    serie: resOpzioni.data.serie || [],
                    profili: resOpzioni.data.profili || [],
                    spessori: resOpzioni.data.spessori || [],
                    verniciature: resOpzioni.data.verniciature || [],
                    persiane: resOpzioni.data.persiane || [],
                    avvolgibili: resOpzioni.data.avvolgibili || [],
                    antoine: resOpzioni.data.antoine || [],
                    zanzariereTipo: resOpzioni.data.zanzariereTipo || [],
                    zanzariereColore: resOpzioni.data.zanzariereColore || [],
                    tipi: resOpzioni.data.tipi || []
                }
            } catch (e) {
                console.error('Errore caricamento configurazione form:', e)
            } finally {
                loadingConfig.value = false
                setTimeout(() => { isFirstLoad = false }, 100)
            }
        }

        onMounted(caricaConfigurazione)
        onUnmounted(() => clearTimeout(debounceTimer))

        // ── Sync props.ordine → datiInterni ─────────────────────────────────
        watch(() => props.ordine, (newVal) => {
            isSyncingFromProps = true
            datiInterni.value = { ...newVal }
            if (datiInterni.value.data)
                datiInterni.value.data = datiInterni.value.data.split('T')[0]
            if (datiInterni.value.dataConsegna)
                datiInterni.value.dataConsegna = datiInterni.value.dataConsegna.split('T')[0]
            nextTick(() => { isSyncingFromProps = false })
        }, { deep: true })

        // ── Autosave ─────────────────────────────────────────────────────────
        watch(() => datiInterni, async () => {
            if (isFirstLoad || isSyncingFromProps) return
            clearTimeout(debounceTimer)
            autoSalvatoIn.value = null
            debounceTimer = setTimeout(async () => {
                const { valid } = await formRef.value.validate()
                if (!valid) return
                autoSalvatoIn.value = 'saving'
                try {
                    emit('salva', buildPayload())
                    autoSalvatoIn.value = 'saved'
                    setTimeout(() => { autoSalvatoIn.value = null }, 3000)
                } catch {
                    autoSalvatoIn.value = 'error'
                }
            }, 1000)
        }, { deep: true })

        // ── Salvataggio manuale ──────────────────────────────────────────────
        const salva = async () => {
            const { valid } = await formRef.value.validate()
            if (valid) emit('salva', buildPayload())
        }

        const buildPayload = () => {
            const dati = { ...datiInterni.value }
            // Pulizia bicolore se verniciatura non è bicolore
            if ((dati.verniciatura || '').toLowerCase() !== 'bicolore') {
                dati.bicoloreInt = ''
                dati.bicoloreEst = ''
            }
            return dati
        }

        // ── Validazione ──────────────────────────────────────────────────────
        const getRules = (campo) => {
            const rules = []
            if (campo.obbligatorio) {
                rules.push(v => !!v || `${campo.etichetta} è obbligatorio`)
            }
            return rules
        }

        const sezioneIncompleta = (sezione) => {
            return sezione.campi.some(c => {
                if (!c.obbligatorio) return false
                const val = datiInterni.value[c.campoKey]
                return !val || (typeof val === 'string' && !val.trim())
            })
        }

        // ── Layout adattivo ──────────────────────────────────────────────────
        // cols su mobile (12 colonne Vuetify)
        const colsCampo = (campo) => {
            if (campo.tipoInput === 'checkbox') return 6
            if (campo.tipoInput === 'textarea') return 12
            return 12
        }

        // cols su sm+ (desktop)
        const colsCampoSm = (campo) => {
            if (campo.tipoInput === 'checkbox') return 2
            if (campo.tipoInput === 'textarea') return 12
            if (campo.tipoInput === 'number') return 3
            if (campo.tipoInput === 'date') return 3
            if (['select', 'autocomplete'].includes(campo.tipoInput)) return 3
            // text: dipende da quanti campi sono nella sezione, default 3
            return 3
        }

        return {
            formRef, datiInterni, loadingConfig,
            configSezioni, sezioniVisibili, opzioni,
            panelAperti, autoSalvatoIn,
            salva, getRules, sezioneIncompleta,
            colsCampo, colsCampoSm
        }
    }
}
</script>

<style scoped>
.form-ordine {
    margin: 0 auto;
}

.form-header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    background: #fafafa;
    border-radius: 8px 8px 0 0;
    position: sticky;
    top: 0;
    z-index: 2;
}

.section-title {
    min-height: 40px !important;
    font-size: 0.82rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    background: #f7f7f8;
    transition: background 0.2s ease;
}

.section-title:hover {
    background: #eef0f3;
}

.v-expansion-panel-text__wrapper {
    padding: 8px 12px 14px !important;
}

.fade-autosave-enter-active,
.fade-autosave-leave-active {
    transition: opacity 0.3s ease;
}

.fade-autosave-enter-from,
.fade-autosave-leave-to {
    opacity: 0;
}
</style>